import moment from "moment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { ICellRendererParams } from "ag-grid-community";
import { FormSubmissionTable } from "../../common/formSubmissionTable";
import type { Outing } from "../../../interfaces/buyerProfileInterface";
import {
  Box,
  Alert,
  Paper,
  Table,
  TableRow,
  Accordion,
  TableHead,
  TableCell,
  TableBody,
  Typography,
  TableContainer,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";

interface OutingsInfoProps {
  outings: Outing[];
}

export const OutingsInfo = ({ outings }: OutingsInfoProps) => {
  function handleListingIdClick(listingId: string) {
    const boardId = listingId[3];
    const mlsNumber = listingId.slice(4);

    if (boardId === "2") {
      const url = `https://onlistings.trreb.ca/searchlistings#search//listing/TREB-${mlsNumber}`;
      window.open(url, "_blank");
    } else {
      const url = `https://www.google.com/search?q=${mlsNumber}`;
      window.open(url, "_blank");
    }
  }

  return (
    <Paper elevation={4} sx={{ p: 3, mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        Outings
      </Typography>
      {outings && outings?.length > 0 ? (
        outings.map((outing: Outing) => (
          <Accordion key={outing.id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography>Outing Status: {outing.status}</Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Typography variant="body2" sx={{ color: "gray" }}>
                    <strong>Created At:</strong>{" "}
                    {moment(outing.createdAt).format("YYYY-MM-DD hh:mm a")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "gray" }}>
                    <strong>Last Updated:</strong>{" "}
                    {moment(outing.updatedAt).format("YYYY-MM-DD hh:mm a")}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Box>
                <Typography variant="subtitle1">Selected Dates</Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date Type</TableCell>
                        <TableCell>Start At</TableCell>
                        <TableCell>End At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Preferred</TableCell>
                        <TableCell>
                          {outing.selectedDates.preferredOutingDate.startAt
                            ? moment(
                                outing.selectedDates.preferredOutingDate.startAt
                              ).format("YYYY-MM-DD hh:mm a")
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          {outing.selectedDates.preferredOutingDate.endAt
                            ? moment(
                                outing.selectedDates.preferredOutingDate.endAt
                              ).format("YYYY-MM-DD hh:mm a")
                            : "N/A"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Backup</TableCell>
                        <TableCell>
                          {outing.selectedDates.backupOutingDate.startAt
                            ? moment(
                                outing.selectedDates.backupOutingDate.startAt
                              ).format("YYYY-MM-DD hh:mm a")
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          {outing.selectedDates.backupOutingDate.endAt
                            ? moment(
                                outing.selectedDates.backupOutingDate.endAt
                              ).format("YYYY-MM-DD hh:mm a")
                            : "N/A"}
                        </TableCell>
                      </TableRow>
                      {outing.selectedDates.confirmedOutingDate && (
                        <TableRow>
                          <TableCell>Confirmed</TableCell>
                          <TableCell>
                            {moment(
                              outing.selectedDates.confirmedOutingDate.startAt
                            ).format("YYYY-MM-DD hh:mm a")}
                          </TableCell>
                          <TableCell>
                            {moment(
                              outing.selectedDates.confirmedOutingDate.endAt
                            ).format("YYYY-MM-DD hh:mm a")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Showings */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Listings</Typography>
                <FormSubmissionTable
                  columnDefs={[
                    {
                      field: "listingId",
                      headerName: "Listing ID",
                      width: 150,
                      cellRenderer: (params: ICellRendererParams<Outing>) => (
                        <span
                          style={{
                            textDecoration: "underline",
                            cursor: "pointer"
                          }}
                          onClick={() => handleListingIdClick(params.value)}
                          onKeyDown={() => handleListingIdClick(params.value)}
                        >
                          {params.value}
                        </span>
                      )
                    },
                    {
                      field: "showingDates.startAt",
                      headerName: "Start At",
                      width: 200,
                      valueGetter: (params) =>
                        params.data?.showingDates?.startAt
                          ? moment(params.data.showingDates.startAt).format(
                              "YYYY-MM-DD hh:mm a"
                            )
                          : "N/A"
                    },
                    {
                      field: "showingDates.endAt",
                      headerName: "End At",
                      width: 200,
                      valueGetter: (params) =>
                        params.data?.showingDates?.endAt
                          ? moment(params.data.showingDates.endAt).format(
                              "YYYY-MM-DD hh:mm a"
                            )
                          : "N/A"
                    },
                    {
                      field: "offerPlaced",
                      headerName: "Offer Placed",
                      width: 200,
                      valueGetter: (params) =>
                        params.data?.offerPlaced ? "Yes" : "No"
                    }
                  ]}
                  pageSize={10}
                  rowData={outing.showings}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <Alert severity="error">No showings found.</Alert>
      )}
    </Paper>
  );
};
